import styled from 'styled-components';

// Components
import { ResumeLoadImages } from '../ResumeLoadImages';
import { Typography } from '../Typography';
import { Definition } from '../Definition';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  gap: ${({ theme }) => theme.spaces.xxxl}rem;
  padding: ${({ theme }) => theme.spaces.xl}rem;
  background-color: ${({ theme }) =>
    theme.colors.backgroundLila}; /* CAMBIO: Color lila según Figma */
  border-radius: 0.8rem;
  @media (max-width: ${({ theme }) => theme.breakPoints.smallDesktop * theme.percentBaseResponsive}rem) {
    flex-direction: row;
    padding: ${({ theme }) => theme.spaces.xl}rem;
    width: 100%;
    gap: ${({ theme }) => theme.spaces.l}rem;
  }

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    flex-direction: column;
  }
`;

export const SubContainer = styled.div`
  /*
   * container-type: inline-size habilita @container queries en hijos.
   * Las usamos en BasicInfoColumns/RoadConditionColumns para apilar texto+extra
   * cuando este SubContainer es demasiado estrecho para tenerlos lado a lado
   * sin solaparse (ej: monitor 24" donde el viewport supera el smallDesktop
   * media query pero el SubContainer aún se queda por debajo de ~900px).
   */
  container-type: inline-size;
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.smallDesktop * theme.percentBaseResponsive}rem) {
    flex: 1;
  }
`;

export const StyledTypography = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spaces.s}rem;
`;

/*
 * Las dos secciones del card tienen necesidades opuestas:
 * - basicInfo: definitions con valores largos a la izquierda + chip-box compacto a la derecha
 *   → texto debe crecer, multimedia debe ajustarse al chip-box (max 27rem).
 * - roadCondition: definitions cortas a la izquierda + textarea descripción a la derecha
 *   → texto debe ocupar lo justo, textarea debe aprovechar el ancho.
 * Por eso exponemos dos variantes de grid en lugar de una sola.
 */
const baseColumnsContainer = `
  display: grid;
  align-items: start;
`;

/*
 * Threshold por encima del cual aún hay sitio cómodo para texto + extra lado a lado.
 * 75rem = 750px (root font-size 10px). Cálculo del mínimo de colisión:
 *   - Title "Medio de conocimiento:" semibold ≈ 200px
 *   - Value largo "Recorregut vigilància" semibold ≈ 200px
 *   - Gap (l = 3.2rem) = 32px
 *   - Chip-box max-width = 27rem = 270px
 *   - TOTAL ≈ 702px → threshold 750px deja un pequeño margen y solo apila cuando
 *     realmente no hay sitio (no antes).
 */
const COLUMNS_STACK_THRESHOLD = '75rem';

export const BasicInfoColumns = styled.div`
  ${baseColumnsContainer}
  /* 1fr auto: definitions ocupan el espacio disponible; multimedia al ancho de su chip-box. */
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${({ theme }) => theme.spaces.l}rem;

  @container (max-width: ${COLUMNS_STACK_THRESHOLD}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spaces.xxxs}rem;
  }

  /* Fallback de viewport para navegadores sin soporte de @container queries. */
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.smallDesktop * theme.percentBaseResponsive}rem) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spaces.xxxs}rem;
  }
`;

export const RoadConditionColumns = styled.div`
  ${baseColumnsContainer}
  /* auto 1fr: definitions al ancho de su contenido; textarea ocupa el espacio restante. */
  grid-template-columns: auto minmax(0, 1fr);
  gap: ${({ theme }) => theme.spaces.l}rem;

  @container (max-width: ${COLUMNS_STACK_THRESHOLD}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spaces.xxxs}rem;
  }

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.smallDesktop * theme.percentBaseResponsive}rem) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spaces.xxxs}rem;
  }
`;

/**
 * @deprecated mantenido por compatibilidad con el storybook y posibles imports externos.
 * Usa BasicInfoColumns o RoadConditionColumns en componentes nuevos.
 */
export const ColumnsContainer = RoadConditionColumns;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spaces.xxxs}rem;
  min-width: 0;
`;

export const ImagesList = styled(ResumeLoadImages)`
  border: 0;
`;

// Espejo de PersonDefinition del resumen de Actuación: el valor es un nombre de
// persona, sin tope de longitud. Aquí la fila mide 424-456 px y hoy cabe en una
// línea en las tres resoluciones, pero un nombre más largo la partiría igual.
export const PersonDefinition = styled(Definition)`
  flex-wrap: wrap;
`;
